"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SessionContext = exports.SessionType = exports.SessionStatus = void 0;
exports.SessionContextProvider = SessionContextProvider;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const native_1 = require("@react-navigation/native");
const Authentication_1 = __importDefault(require("../shared/Authentication"));
const SingleSignOn_1 = require("../shared/SingleSignOn");
const Helper_1 = require("../Helper");
const DeepLinkContext_1 = require("./DeepLinkContext");
var SessionStatus;
(function (SessionStatus) {
    SessionStatus[SessionStatus["Initializing"] = 0] = "Initializing";
    SessionStatus[SessionStatus["UnAuthenticated"] = 1] = "UnAuthenticated";
    SessionStatus[SessionStatus["Authenticated"] = 2] = "Authenticated";
})(SessionStatus || (exports.SessionStatus = SessionStatus = {}));
;
var SessionType;
(function (SessionType) {
    SessionType[SessionType["Unknown"] = 0] = "Unknown";
    SessionType[SessionType["Employee"] = 1] = "Employee";
    SessionType[SessionType["Prospect"] = 2] = "Prospect";
})(SessionType || (exports.SessionType = SessionType = {}));
;
exports.SessionContext = react_1.default.createContext(null);
function SessionContextProvider(props) {
    const deepLinkContext = react_1.default.useContext(DeepLinkContext_1.DeepLinkContext);
    const navigation = react_1.default.useRef(null);
    const route = react_1.default.useRef(null);
    const [status, setStatus] = react_1.default.useState(SessionStatus.Initializing);
    const [type, setType] = react_1.default.useState(SessionType.Unknown);
    const [userId, setUserId] = react_1.default.useState(null);
    const [userToken, setUserToken] = react_1.default.useState(null);
    const samlRedirected = react_1.default.useRef(false);
    const navigate = (screen, data) => {
        if (navigation.current) {
            navigation.current.dispatch(native_1.CommonActions.reset({
                index: 0,
                routes: [{
                        name: screen,
                        params: data
                    }]
            }));
        }
    };
    const autoNavigate = (status, route) => {
        switch (status) {
            case SessionStatus.Authenticated:
                if (route?.name == 'Login' && route?.params?.SAMLRequest) {
                    if (!samlRedirected.current) {
                        samlRedirected.current = true;
                        SingleSignOn_1.SingleSignOn.RedirectSaml();
                    }
                }
                else if (((route?.name == 'Login' && !route?.params?.mode) ||
                    (route?.name == 'Register' && !route?.params?.mfa)) &&
                    !!!deepLinkContext.deepLink.url) {
                    navigate('Content');
                }
                break;
            case SessionStatus.UnAuthenticated:
                if (route?.name != 'Login' &&
                    route?.name != 'Register') {
                    navigate('Login');
                }
                break;
        }
    };
    // Get time since the last full authentication (not token refresh).
    const getTimeSinceAuth = react_1.default.useCallback(() => {
        if (userToken) {
            var timestamp = Math.floor((new Date()).getTime() / 1000);
            var diff = (timestamp - userToken.claims.auth_time);
            return {
                total: {
                    seconds: diff,
                    minutes: diff / 60,
                    hours: diff / 60 / 60
                }
            };
        }
        else {
            return null;
        }
    }, [userToken]);
    // Determine whether the session should be timed out.
    const getSessionTimeout = react_1.default.useCallback(() => {
        let elapsed = getTimeSinceAuth();
        if (elapsed) {
            if (Helper_1.Helper.isPlatformWeb) {
                return (elapsed.total.hours > 12);
            }
            else { // Native
                return (elapsed.total.hours > 1);
            }
        }
        else {
            return false;
        }
    }, [userToken]);
    const onNavigation = react_1.default.useCallback((n, e) => {
        navigation.current = n;
        route.current = e.data?.state?.routes[e.data?.state?.index];
        if (getSessionTimeout()) {
            Authentication_1.default.SignOut(false);
        }
        else {
            autoNavigate(status, route.current);
        }
    }, [userToken, status]);
    const onCheckSessionTimeout = react_1.default.useCallback(() => {
        if (getSessionTimeout()) {
            Authentication_1.default.SignOut(false);
            return true;
        }
        else {
            return false;
        }
    }, [userToken]);
    react_1.default.useEffect(() => {
        autoNavigate(status, route.current);
    }, [status]);
    react_1.default.useEffect(() => {
        // AuthStateChanged triggered by sign in or sign out.
        return Authentication_1.default.OnAuthStateChanged((session) => {
            let clear = () => {
                setUserId(null);
                setUserToken(null);
            };
            if (session) {
                session.getIdTokenResult().then((t) => {
                    clear();
                    setUserId(session.uid);
                    setUserToken(t);
                    setStatus(SessionStatus.Authenticated);
                    if (t?.claims?.employee)
                        setType(SessionType.Employee);
                    else if (t?.claims?.prospect)
                        setType(SessionType.Prospect);
                    else
                        setType(SessionType.Unknown);
                });
            }
            else {
                clear();
                setStatus(SessionStatus.UnAuthenticated);
            }
        });
    }, []);
    react_1.default.useEffect(() => {
        // IdTokenChanged triggered by sign in, sign out, or token refresh.
        return Authentication_1.default.OnIdTokenChanged((session) => {
            if (session) {
                session.getIdTokenResult().then((t) => setUserToken(t));
            }
            else {
                setUserToken(null);
            }
        });
    }, []);
    return ((0, jsx_runtime_1.jsx)(exports.SessionContext.Provider, { value: {
            status: status,
            type: type,
            userId: userId, // ID for the logged in user (async).
            userToken: userToken, // Data for the logged in user (async).
            initializing: (status == SessionStatus.Initializing), // Checkin
            getTimeSinceAuth: () => getTimeSinceAuth(),
            getSessionTimeout: () => getSessionTimeout(),
            navigate: navigate,
            onNavigation: (n, e) => onNavigation(n, e), // Notifies SessionContext of a navigation change.
            onCheckSessionTimeout: () => onCheckSessionTimeout()
        }, children: props.children }));
}
;
